import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { fmtDate } from "../lib/ui";
import { Plus, Pencil } from "lucide-react";

const TYPE_CODES = ["individual", "team", "organisation"];

// ---------------- Signup Settings ----------------
export function SignupSettings() {
  const [s, setS] = useState(null);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  useEffect(() => { api.get("/platform/signup-settings").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <div className="text-slate-400 text-sm">Loading…</div>;

  const toggleType = (code) => {
    const cur = s.allowed_account_types || [];
    set("allowed_account_types", cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]);
  };
  const save = async () => {
    try { await api.put("/platform/signup-settings", s); toast.success("Signup settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="signup-settings">
      <div className="flex items-center justify-between bg-white border rounded-xl p-4">
        <div><div className="font-medium text-sm">Public sign-up</div><div className="text-xs text-slate-500">Allow anyone to create a new account</div></div>
        <Switch data-testid="ss-public-signup" checked={!!s.public_signup_enabled} onCheckedChange={(v) => set("public_signup_enabled", v)} />
      </div>

      <div className="bg-white border rounded-xl p-4">
        <div className="font-medium text-sm mb-3">Allowed account types</div>
        <div className="flex flex-wrap gap-4">
          {TYPE_CODES.map((c) => (
            <label key={c} className="flex items-center gap-2 text-sm capitalize cursor-pointer">
              <input type="checkbox" data-testid={`ss-type-${c}`} checked={(s.allowed_account_types || []).includes(c)} onChange={() => toggleType(c)} />
              {c}
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white border rounded-xl p-4 space-y-3">
        <div className="font-medium text-sm">Email verification</div>
        {[["active_optional", "Active immediately — verification email optional"],
          ["pending_no_access", "Pending verification — no sign-in until email verified"]].map(([val, lbl]) => (
          <label key={val} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="radio" name="evmode" data-testid={`ss-mode-${val}`} checked={s.email_verification_mode === val} onChange={() => set("email_verification_mode", val)} />
            {lbl}
          </label>
        ))}
        <div className="flex items-center justify-between pt-2">
          <div className="text-sm">Require mobile number at sign-up</div>
          <Switch data-testid="ss-mobile-required" checked={!!s.mobile_verification_required} onCheckedChange={(v) => set("mobile_verification_required", v)} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white border rounded-xl p-4">
        <div className="space-y-1.5">
          <Label>Default role for invited users</Label>
          <Select value={s.default_role} onValueChange={(v) => set("default_role", v)}>
            <SelectTrigger data-testid="ss-default-role"><SelectValue /></SelectTrigger>
            <SelectContent>{["admin", "manager", "employee", "viewer"].map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Invitation expiry (days)</Label>
          <Input type="number" data-testid="ss-invite-expiry" value={s.invite_expiry_days} onChange={(e) => set("invite_expiry_days", Number(e.target.value))} />
        </div>
        <div className="space-y-1.5">
          <Label>Terms URL</Label>
          <Input data-testid="ss-terms-url" value={s.terms_url || ""} onChange={(e) => set("terms_url", e.target.value)} placeholder="https://…" />
        </div>
        <div className="space-y-1.5">
          <Label>Terms version</Label>
          <Input value={s.terms_version || ""} onChange={(e) => set("terms_version", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Privacy URL</Label>
          <Input data-testid="ss-privacy-url" value={s.privacy_url || ""} onChange={(e) => set("privacy_url", e.target.value)} placeholder="https://…" />
        </div>
        <div className="space-y-1.5">
          <Label>Privacy version</Label>
          <Input value={s.privacy_version || ""} onChange={(e) => set("privacy_version", e.target.value)} />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Welcome message</Label>
          <Input data-testid="ss-welcome" value={s.welcome_message || ""} onChange={(e) => set("welcome_message", e.target.value)} />
        </div>
      </div>

      <Button data-testid="ss-save" onClick={save}>Save settings</Button>
    </div>
  );
}

// ---------------- Account Types ----------------
export function AccountTypesAdmin() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [show, setShow] = useState(false);
  const load = () => api.get("/platform/account-types").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const deactivate = async (t) => {
    if (!window.confirm(`Deactivate "${t.name}"? Existing accounts keep working; new sign-ups won't offer it.`)) return;
    try { await api.delete(`/platform/account-types/${t.id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="account-types-admin">
      <div className="flex justify-end mb-3"><Button data-testid="at-new" onClick={() => { setEdit(null); setShow(true); }}><Plus size={16} className="mr-1" /> New Type</Button></div>
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Code</th><th className="px-4 py-2.5">Description</th><th className="px-4 py-2.5">Signup</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5"></th>
          </tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} className="border-b last:border-0" data-testid={`at-row-${t.code}`}>
                <td className="px-4 py-2.5 font-medium">{t.name}</td>
                <td className="px-4 py-2.5 text-slate-500">{t.code}</td>
                <td className="px-4 py-2.5 text-slate-500">{t.description}</td>
                <td className="px-4 py-2.5">{t.signup_enabled ? "Enabled" : "Off"}</td>
                <td className="px-4 py-2.5"><span className={`text-xs rounded-full px-2 py-0.5 ${t.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{t.active ? "Active" : "Inactive"}</span></td>
                <td className="px-4 py-2.5 text-right">
                  <Button variant="ghost" size="sm" onClick={() => { setEdit(t); setShow(true); }}><Pencil size={14} /></Button>
                  {t.active && <Button variant="ghost" size="sm" className="text-red-600" data-testid={`at-deactivate-${t.code}`} onClick={() => deactivate(t)}>Deactivate</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {show && <AccountTypeModal row={edit} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}

function AccountTypeModal({ row, onClose, onSaved }) {
  const [f, setF] = useState(row || { name: "", code: "", description: "", sequence: 99, active: true, signup_enabled: true });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    try {
      if (row) await api.put(`/platform/account-types/${row.id}`, f);
      else await api.post("/platform/account-types", f);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="at-modal">
        <DialogHeader><DialogTitle>{row ? "Edit" : "New"} account type</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Name</Label><Input data-testid="at-name" value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
          {!row && <div className="space-y-1.5"><Label>Code (optional)</Label><Input data-testid="at-code" value={f.code} onChange={(e) => set("code", e.target.value)} placeholder="auto from name" /></div>}
          <div className="space-y-1.5"><Label>Description</Label><Input data-testid="at-desc" value={f.description} onChange={(e) => set("description", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Sequence</Label><Input type="number" value={f.sequence} onChange={(e) => set("sequence", Number(e.target.value))} /></div>
          <div className="flex items-center justify-between"><span className="text-sm">Active</span><Switch checked={!!f.active} onCheckedChange={(v) => set("active", v)} /></div>
          <div className="flex items-center justify-between"><span className="text-sm">Enabled at sign-up</span><Switch checked={!!f.signup_enabled} onCheckedChange={(v) => set("signup_enabled", v)} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="at-save" onClick={save}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Accounts (tenants) ----------------
const STATUS_STYLE = {
  active: "bg-emerald-50 text-emerald-700", pending_verification: "bg-amber-50 text-amber-700",
  suspended: "bg-red-50 text-red-700", inactive: "bg-slate-100 text-slate-500", closed: "bg-slate-100 text-slate-500",
};

export function AccountsAdmin() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const load = () => {
    const p = {};
    if (status !== "all") p.status = status;
    if (type !== "all") p.type = type;
    if (q.trim()) p.q = q.trim();
    api.get("/platform/accounts", { params: p }).then(({ data }) => setRows(data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, type]);

  const changeStatus = async (a, st) => {
    try { await api.post(`/platform/accounts/${a.id}/status`, { status: st }); toast.success("Account updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="accounts-admin">
      <div className="flex flex-wrap gap-2 mb-3">
        <Input data-testid="acc-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search accounts…" className="w-56 h-9" />
        <Select value={status} onValueChange={setStatus}><SelectTrigger data-testid="acc-status-filter" className="w-40 h-9"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{["active", "pending_verification", "suspended", "inactive", "closed"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select>
        <Select value={type} onValueChange={setType}><SelectTrigger data-testid="acc-type-filter" className="w-40 h-9"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All types</SelectItem>{TYPE_CODES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select>
      </div>
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <th className="px-4 py-2.5">Account</th><th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Owner</th><th className="px-4 py-2.5">Members</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Created</th><th className="px-4 py-2.5"></th>
          </tr></thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} className="border-b last:border-0" data-testid={`acc-row-${a.human_id}`}>
                <td className="px-4 py-2.5 font-medium">{a.name}{a.is_legacy && <span className="ml-2 text-xs bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">Legacy</span>}<div className="text-xs text-slate-400">{a.human_id}</div></td>
                <td className="px-4 py-2.5 capitalize">{a.type}</td>
                <td className="px-4 py-2.5 text-slate-500">{a.owner_email || "—"}</td>
                <td className="px-4 py-2.5">{a.member_count}</td>
                <td className="px-4 py-2.5"><span className={`text-xs rounded-full px-2 py-0.5 capitalize ${STATUS_STYLE[a.status] || "bg-slate-100"}`}>{(a.status || "").replace(/_/g, " ")}</span></td>
                <td className="px-4 py-2.5 text-slate-500">{fmtDate(a.created_at)}</td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" data-testid={`acc-view-${a.human_id}`} onClick={() => api.get(`/platform/accounts/${a.id}`).then(({ data }) => setDetail(data))}>View</Button>
                  {!a.is_legacy && (
                    <Select value="" onValueChange={(v) => changeStatus(a, v)}>
                      <SelectTrigger data-testid={`acc-status-${a.human_id}`} className="w-28 h-8 inline-flex ml-1"><SelectValue placeholder="Actions" /></SelectTrigger>
                      <SelectContent>
                        {a.status !== "active" && <SelectItem value="active">Activate</SelectItem>}
                        {a.status !== "suspended" && <SelectItem value="suspended">Suspend</SelectItem>}
                        {a.status !== "inactive" && <SelectItem value="inactive">Deactivate</SelectItem>}
                        {a.status !== "closed" && <SelectItem value="closed">Close</SelectItem>}
                      </SelectContent>
                    </Select>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">No accounts found.</td></tr>}
          </tbody>
        </table>
      </div>
      {detail && (
        <Dialog open onOpenChange={() => setDetail(null)}>
          <DialogContent data-testid="acc-detail">
            <DialogHeader><DialogTitle>{detail.name} · {detail.human_id}</DialogTitle></DialogHeader>
            <div className="text-sm space-y-1 mb-3">
              <div><span className="text-slate-500">Type:</span> <span className="capitalize">{detail.type}</span></div>
              <div><span className="text-slate-500">Status:</span> <span className="capitalize">{(detail.status || "").replace(/_/g, " ")}</span></div>
              <div><span className="text-slate-500">Owner:</span> {detail.owner_email || "—"}</div>
            </div>
            <div className="font-medium text-sm mb-1">Members ({detail.members?.length || 0})</div>
            <div className="border rounded-lg max-h-64 overflow-y-auto">
              {(detail.members || []).map((m) => (
                <div key={m.id} className="flex items-center justify-between px-3 py-2 border-b last:border-0 text-sm">
                  <div><div className="font-medium">{m.full_name}</div><div className="text-xs text-slate-500">{m.email}</div></div>
                  <div className="text-xs text-slate-500 capitalize">{m.account_role || m.role}{m.email_verified ? "" : " · unverified"}</div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ---------------- Master-Admin Visibility Settings ----------------
const MAV_ENTITIES = [
  { key: "organizations", label: "Organizations" },
  { key: "workspaces", label: "Workspaces" },
  { key: "projects", label: "Projects" },
  { key: "tasks", label: "Tasks" },
];
const MAV_DEFAULT = MAV_ENTITIES.reduce((a, e) => ({ ...a, [`${e.key}_my`]: true, [`${e.key}_others`]: true }), {});

export function VisibilitySettings() {
  const [s, setS] = useState(null);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  useEffect(() => { api.get("/platform/visibility-settings").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <div className="text-slate-400 text-sm">Loading…</div>;
  const save = async (payload, msg) => {
    try {
      const body = Object.fromEntries(Object.keys(MAV_DEFAULT).map((k) => [k, !!(payload || s)[k]]));
      const { data } = await api.put("/platform/visibility-settings", body);
      setS(data); toast.success(msg || "Visibility settings saved.");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="max-w-2xl space-y-4" data-testid="visibility-settings">
      <div className="bg-white border rounded-xl p-5">
        <div className="text-sm text-slate-500 mb-4">Choose which records appear in your Master Admin views (lists, search, dashboard counts, reports & selectors). This is a view preference only — it never deletes, archives or changes access.</div>
        <div className="space-y-4">
          {MAV_ENTITIES.map((e) => (
            <div key={e.key} className="flex items-center justify-between border-b last:border-0 pb-3 last:pb-0">
              <div className="font-medium text-sm">{e.label}</div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" data-testid={`vis-${e.key}-my`} checked={!!s[`${e.key}_my`]} onChange={(ev) => set(`${e.key}_my`, ev.target.checked)} /> My Records
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" data-testid={`vis-${e.key}-others`} checked={!!s[`${e.key}_others`]} onChange={(ev) => set(`${e.key}_others`, ev.target.checked)} /> Other Users' Records
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <Button data-testid="vis-save" onClick={() => save()}>Save</Button>
        <Button variant="outline" data-testid="vis-reset" onClick={() => save(MAV_DEFAULT, "Reset to default.")}>Reset to Default</Button>
      </div>
    </div>
  );
}

